import "react-app-polyfill/ie11"; // For IE 11 support
import "react-app-polyfill/stable";
import React from "react";
import { createRoot } from "react-dom/client";
import { RecoilRoot } from "recoil";
import { ThemeProvider } from "styled-components";
import { GlobalStyle, theme } from "./components/styles";
import { RouterProvider } from "react-router-dom";
import routes from "routes";
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import Toast from "components/foundation/Toast";
import BridgeProvider from "config/webView";
import dayjs from "dayjs";
import "dayjs/locale/ko"; // 한국어 로컬 파일 import
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "@toast-ui/editor/dist/toastui-editor.css";
import { Font } from "@react-pdf/renderer";

import AlertProvider from "components/Common/Alert/AlertProvider";
import { useNetworkStatus } from "features/common/hooks/useNetworkStatus";
import { modalStore } from "components/Common/Alert/AlertManager";

import FontPretendardThin from "assets/fonts/Pretendard-Thin.ttf";
import FontPretendardExtraLight from "assets/fonts/Pretendard-ExtraLight.ttf";
import FontPretendardLight from "assets/fonts/Pretendard-Light.ttf";
import FontPretendardRegular from "assets/fonts/Pretendard-Regular.ttf";
import FontPretendardMedium from "assets/fonts/Pretendard-Medium.ttf";
import FontPretendardSemiBold from "assets/fonts/Pretendard-SemiBold.ttf";
import FontPretendardBold from "assets/fonts/Pretendard-Bold.ttf";
import FontPretendardExtraBold from "assets/fonts/Pretendard-ExtraBold.ttf";
import FontPretendardBlack from "assets/fonts/Pretendard-Black.ttf";

dayjs.locale("ko"); // 한국어로 설정
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Seoul");

const App = () => {
  useNetworkStatus(); // 네트워크 상태 감지

  return (
    <RecoilRoot>
      <BridgeProvider>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <Toast.Provider swipeDirection="down" duration={2000}>
              <GlobalStyle />
              <RouterProvider router={routes} />
              <AlertProvider />
            </Toast.Provider>
          </QueryClientProvider>
        </ThemeProvider>
      </BridgeProvider>
    </RecoilRoot>
  );
};

const container = document.getElementById("root");
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = createRoot(container!);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0, // API 요청 실패 시 재시도하지 않습니다.
      refetchOnWindowFocus: false, // 윈도우가 다시 포커스돼도 데이터를 새로 호출하지 않습니다.
    },
  },
  mutationCache: new MutationCache({
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      if (
        error instanceof Error &&
        (error.message === "ERR_NETWORK" ||
          error.message === "ERR_INTERNET_DISCONNECTED")
      ) {
        modalStore.register({
          id: "network-error-modal",
          title: `네트워크 연결 오류`,
          content: "인터넷 연결이 불안정합니다. 연결 상태를 확인해 주세요.",
        });
      }
    },
  }),
});

root.render(<App />);

Font.register({
  family: "Pretendard",
  fonts: [
    {
      src: FontPretendardRegular,
    },
    {
      src: FontPretendardThin,
      fontWeight: 100,
    },
    {
      src: FontPretendardExtraLight,
      fontWeight: 200,
    },
    {
      src: FontPretendardLight,
      fontWeight: 300,
    },
    {
      src: FontPretendardMedium,
      fontWeight: 500,
    },
    {
      src: FontPretendardSemiBold,
      fontWeight: 600,
    },
    {
      src: FontPretendardBold,
      fontWeight: 700,
    },
    {
      src: FontPretendardExtraBold,
      fontWeight: 800,
    },
    {
      src: FontPretendardBlack,
      fontWeight: 900,
    },
  ],
});
